/**
 * The dashboard: what needs a human in the next three hours. Not a BI report.
 *
 * Every tile is a **queue count from the server**, and it clicks through to the queue it came
 * from. A number nobody can act on is decoration, and decoration on the first screen of an ops
 * console trains people to skim past the numbers that matter.
 *
 * **The counts are `total` from a filtered read, not a length.** Counting the rows in hand would
 * report "50 unaccepted" forever, which is the most reassuring possible way to be wrong.
 *
 * **What has no endpoint is not here.** Money payable, the trial balance and supply liquidity once
 * had tiles showing invented numbers; they were removed rather than zeroed, because a plausible
 * figure on an ops dashboard gets repeated in a meeting.
 *
 * All three are back, over endpoints that now exist — and they are back as the numbers the
 * endpoints actually answer, not the ones the old tiles pretended to. **There is no rupee-sum
 * payable figure**, because `/v1/admin/payables` is a paged list and summing the page in hand
 * would report the first 25 payables as the whole book — the most confident possible way to be
 * wrong. What it can answer honestly is *how many* are stuck, so that is the tile. Supply
 * liquidity is back too, as the count of thin puja × city cells over the E36 coverage read.
 *
 * **The money tiles are finance's.** The dashboard is shown to every scope; these two endpoints
 * admit `finance` and `superadmin`. Asking for them as `support` would put two error tiles on the
 * first screen of the console — so they are not rendered, not disabled and not failed. The
 * coverage tile follows the same rule for the scopes its endpoint admits.
 *
 * **Today's bookings by state and supply liquidity** are the design's two week-ahead panels, each
 * over its own read and each shown only to the scopes that read admits.
 */
import { Button, Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useKycQueue } from '../lib/api/hooks/kyc';
import { useBookingBoard, useBookingsByState } from '../lib/api/hooks/bookings';
import { useCoverageGrid, useSupplyLiquidity } from '../lib/api/hooks/supply';
import { usePayables } from '../lib/api/hooks/payables';
import { useMoneySummary, useTrialBalance } from '../lib/api/hooks/ledger';
import { formatInr } from '../lib/money';
import { ceremonyAt, clockTo, statusLabel, statusTone } from './DispatchBoard';
import { coverLabel, coverTone, stateBands } from './opsBoard';
import { ANALYTICS_SCOPES, DashboardAnalytics } from './DashboardAnalytics';
import { DispatchOpsTiles } from './HealthStrip';

/** Sent to a pandit and not yet taken. The queue dispatch is failing to clear. */
const UNACCEPTED = ['requested', 'matching', 'acceptance_expired'];

/** Nobody is coming, or the pandit walked. Both need a person today. */
const STUCK = ['no_provider_available', 'reassignment_required'];

/** Going ahead — the ones with a ceremony to actually turn up to. */
const LIVE = ['confirmed', 'provider_en_route', 'in_progress'];

/**
 * Payables that are not moving, and each is a pandit who has done the work and not been paid.
 *
 * `on_hold_dispute` is somebody's decision to stop the money; `held` past its issue window is
 * nobody's — it is the one that rots quietly, which is exactly what a dashboard is for. They are
 * counted together because the operator's next action is the same: open the payouts queue.
 */
const STALLED_PAYABLE = 'on_hold_dispute';

/** The two scopes the server admits to the payable board and the trial balance. */
export const MONEY_SCOPES = ['superadmin', 'finance'] as const;

/** The scopes the server admits to `/v1/admin/supply/coverage`. */
export const COVERAGE_SCOPES = ['superadmin', 'supply_kyc', 'ops_dispatch'] as const;

/** The scopes the server admits to the bookings reads, `/v1/admin/bookings/by-state` among them. */
export const BOARD_SCOPES = ['superadmin', 'ops_dispatch', 'support'] as const;

/** The scopes the server admits to `/v1/admin/kyc/queue`. */
export const KYC_SCOPES = ['superadmin', 'supply_kyc'] as const;

/** The month a trial balance is asked for: the current one, in IST, as `YYYY-MM`. */
export function currentMonthIst(now: Date = new Date()): string {
  const ist = new Date(now.getTime() + 5.5 * 3600_000);
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * The end of today in IST, as the instant the server should compare against.
 *
 * IST rather than the browser's zone: every ceremony on this platform happens in it, and an
 * operator in Patna and one working late from elsewhere must see the same "today".
 */
export function endOfDayIst(now: Date = new Date()): string {
  const ist = new Date(now.getTime() + 5.5 * 3600_000);
  const midnight = Date.UTC(
    ist.getUTCFullYear(),
    ist.getUTCMonth(),
    ist.getUTCDate() + 1,
    0,
    0,
    0,
  );
  return new Date(midnight - 5.5 * 3600_000).toISOString();
}

/**
 * Payables stopped by a dispute — a count, and it clicks through to the queue that clears them.
 *
 * A count rather than an amount, and the difference is not a shortcut: the endpoint is a paged
 * list, so the only rupee figure available here would be the sum of the first page. `total` is the
 * whole book, which is the number an operator can act on.
 */
function StalledPayables() {
  const { go } = useConsole();
  const stalled = usePayables({ payable_status: STALLED_PAYABLE, page: 1, page_size: 1 });
  return (
    <StatTile
      label="Payables in dispute"
      value={stalled.isSuccess ? stalled.data.total : '—'}
      note="A pandit has done the work and the money has stopped"
      tone={stalled.isSuccess && stalled.data.total > 0 ? 'error' : 'default'}
      onClick={() => go('payouts')}
    />
  );
}

/**
 * Whether this month's books balance.
 *
 * ⛔ The tile shows the **delta**, and the delta should be exactly zero. A non-zero one is a
 * reconciliation break — money recorded on one side and not the other — and it is the single
 * number on this console that means "stop and find out why". Showing the totals instead would
 * make it a report; showing the difference makes it an alarm.
 */
function BooksBalance() {
  const { go } = useConsole();
  const balance = useTrialBalance(currentMonthIst());
  const delta = balance.isSuccess ? balance.data.delta_paise : null;
  return (
    <StatTile
      label="Books this month"
      // ⛔ `paise: true`. A one-paisa break is the classic reconciliation break, and the default
      // format drops a zero fraction — so a ₹0.01 delta would render as "₹0", an alarm showing
      // nothing. Money is integer paise everywhere in this product; here it has to LOOK like it.
      value={delta === null ? '—' : delta === 0 ? 'Balanced' : formatInr(delta, { paise: true })}
      note={delta === 0 ? 'Debits equal credits' : 'Debits and credits disagree — find out why'}
      tone={delta !== null && delta !== 0 ? 'error' : 'default'}
      onClick={() => go('ledger')}
    />
  );
}

/**
 * Captured and platform revenue this month (S05, 05-admin/05 §5 item 4), from the ledger-backed
 * summary (R303) — never from analytics events. Each is footed with where it comes from and opens
 * the ledger, which is where anybody questioning the number goes next.
 */
function MoneySummaryTiles() {
  const { go } = useConsole();
  const summary = useMoneySummary(currentMonthIst());
  const value = (paise: number | undefined) => (paise === undefined ? '—' : formatInr(paise));
  return (
    <>
      <StatTile
        label="Captured this month"
        value={value(summary.data?.captured_paise)}
        note="From the finance ledger"
        onClick={() => go('ledger')}
      />
      <StatTile
        label="Platform revenue this month"
        value={value(summary.data?.platform_revenue_paise)}
        note="From the finance ledger"
        onClick={() => go('ledger')}
      />
    </>
  );
}

/**
 * Supply liquidity — how many puja × city cells have fewer than three pandits.
 *
 * A count of cells, not a percentage: the grid is launched cities times active pujas, and "12 thin
 * cells" is a recruiting list where "94% covered" is a reassurance. The cells arrive with zeros
 * present, so an empty cell is counted, never skipped.
 */
function ThinCoverage() {
  const { go } = useConsole();
  const grid = useCoverageGrid();
  const thin = grid.isSuccess
    ? grid.data.cells.filter((cell) => cell.pandit_count < grid.data.thin_below).length
    : null;
  return (
    <StatTile
      label="Thin coverage"
      value={thin ?? '—'}
      note={
        grid.isSuccess
          ? `Puja × city pairs with fewer than ${grid.data.thin_below} pandits`
          : 'Puja × city pairs too thin to rely on'
      }
      tone={thin !== null && thin > 0 ? 'error' : 'default'}
      onClick={() => go('supply')}
    />
  );
}

export function Dashboard() {
  const { go } = useConsole();

  const { scope } = useConsole();
  const seesMoney = (MONEY_SCOPES as readonly string[]).includes(scope);
  const seesCoverage = (COVERAGE_SCOPES as readonly string[]).includes(scope);
  const seesBoard = (BOARD_SCOPES as readonly string[]).includes(scope);
  const seesKyc = (KYC_SCOPES as readonly string[]).includes(scope);
  const seesAnalytics = (ANALYTICS_SCOPES as readonly string[]).includes(scope);
  /* Asked only of a scope the server admits: a tile for a queue you cannot read is a 403 on every
     dashboard load and a `—` that never resolves (found by e2e/scopes.spec.ts). */
  const unaccepted = useBookingBoard({ status: UNACCEPTED, page: 1, page_size: 6 }, seesBoard);
  const stuck = useBookingBoard({ status: STUCK, page: 1, page_size: 6 }, seesBoard);
  const today = useBookingBoard(
    { status: LIVE, starts_until: endOfDayIst(), page: 1, page_size: 1 },
    seesBoard,
  );
  const kyc = useKycQueue({ page: 1, page_size: 1, status: 'in_review' }, seesKyc);

  /* A count we could not read is `—`, never `0`. Zero is a fact about the queue; a failed read is
     a fact about us, and an ops desk that cannot tell them apart stops trusting the tile. */
  const count = (query: { isSuccess: boolean; data?: { total: number } }) =>
    query.isSuccess ? query.data!.total : '—';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)' }}>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Operational counters, not analytics — every tile links to the queue it came from.
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        {seesBoard && (
          <>
            <StatTile
              label="Ceremonies today"
              value={count(today)}
              note="Confirmed, en route or under way"
              onClick={() => go('dispatch')}
            />
            <StatTile
              label="Unaccepted"
              value={count(unaccepted)}
              note="Requested, matching or expired with nobody taking it"
              tone="error"
              onClick={() => go('dispatch')}
            />
            <StatTile
              label="Stuck bookings"
              value={count(stuck)}
              note="No provider found, or the pandit withdrew"
              tone="error"
              onClick={() => go('dispatch')}
            />
            <DispatchOpsTiles />
          </>
        )}
        {seesKyc && (
          <StatTile
            label="KYC in review"
            value={kyc.isSuccess ? kyc.data.total : '—'}
            note="Waiting on a reviewer"
            onClick={() => go('kyc')}
          />
        )}
        {seesMoney && <StalledPayables />}
        {seesMoney && <MoneySummaryTiles />}
        {seesMoney && <BooksBalance />}
        {seesCoverage && <ThinCoverage />}
      </div>

      {(seesBoard || seesCoverage) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
            gap: 'var(--sp-lg)',
          }}
        >
          {seesBoard && <BookingsByState />}
          {seesCoverage && <SupplyLiquidity />}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: 'var(--sp-lg)',
        }}
      >
        <Section
          title="Unaccepted requests"
          note="Soonest ceremony first — the clock is time to the muhurat"
          pad={false}
        >
          <BookingQueue query={unaccepted} onOpen={() => go('dispatch')} empty="Everything requested has been taken." />
        </Section>

        <Section title="Stuck bookings" note="A human has to intervene" pad={false}>
          <BookingQueue query={stuck} onOpen={() => go('dispatch')} empty="Nothing is stuck." />
        </Section>
      </div>

      {seesAnalytics && <DashboardAnalytics />}

      <Note>
        Thin coverage counts the cells of the coverage grid, not a percentage of it. There is no
        rupee total payable — the payables endpoint is a paged list, and the sum of one page is
        not the book.
      </Note>
    </>
  );
}

const BAR: Record<string, string> = {
  error: 'var(--c-error)',
  warn: 'var(--c-warn)',
  success: 'var(--c-success)',
  info: 'var(--c-info)',
  neutral: 'var(--c-ink3)',
};

function BookingsByState() {
  const query = useBookingsByState();
  const bands = query.isSuccess ? stateBands(query.data.today) : [];
  // The longest bar is full width; an all-zero day draws empty bars rather than dividing by zero.
  const widest = Math.max(1, ...bands.map((b) => b.count));
  return (
    <Section
      title="Today's bookings by state"
      note={query.isSuccess ? `${query.data.week_total} in the next 7 days` : undefined}
    >
      {query.isPending && <LoadingState message="Counting today's bookings…" />}
      {query.isError && <ErrorState title="Could not count bookings" cause={errorMessage(query.error)} />}
      {query.isSuccess && (
        <div style={{ display: 'grid', gap: 6 }}>
          {bands.map((band) => (
            <div
              key={band.label}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)', fontSize: 'var(--fs-admin-ui)' }}
            >
              <span style={{ flex: '0 0 150px', color: 'var(--c-ink2)' }}>{band.label}</span>
              <div style={{ flex: 1, height: 8, background: 'var(--c-sunk)', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  data-testid={`band-${band.label}`}
                  style={{ width: `${(band.count / widest) * 100}%`, height: '100%', background: BAR[band.tone] }}
                />
              </div>
              <span className="tabular" style={{ flex: '0 0 40px', textAlign: 'right', color: 'var(--c-ink)' }}>
                {band.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function SupplyLiquidity() {
  const { go } = useConsole();
  const query = useSupplyLiquidity();
  const rows = query.data?.items ?? [];
  return (
    <Section
      title="Supply liquidity"
      note="Dispatchable pandits vs open demand, next 7 days"
      pad={false}
      actions={
        <Button size="sm" variant="ghost" icon="users" onClick={() => go('pandits')}>
          Directory
        </Button>
      }
    >
      {query.isPending && <LoadingState message="Reading supply…" />}
      {query.isError && <ErrorState title="Could not read supply" cause={errorMessage(query.error)} />}
      {query.isSuccess && rows.length === 0 && (
        <EmptyState icon="map-pin" title="No launched city" body="Launch a city to see its supply here." inline />
      )}
      {rows.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'city', label: 'City' },
            { key: 'active', label: 'Dispatchable', align: 'right' },
            { key: 'demand', label: 'Open demand', align: 'right' },
            { key: 'cover', label: 'Coverage' },
          ]}
          rows={rows.map((c) => ({
            id: c.city_id,
            city: c.city_name,
            active: <span className="tabular">{c.dispatchable}</span>,
            demand: <span className="tabular">{c.open_demand}</span>,
            cover: <StatusChip tone={coverTone(c.covered_share)} label={coverLabel(c.covered_share)} />,
          }))}
        />
      )}
    </Section>
  );
}

interface QueueProps {
  query: ReturnType<typeof useBookingBoard>;
  onOpen: () => void;
  empty: string;
}

function BookingQueue({ query, onOpen, empty }: QueueProps) {
  const rows = query.data?.items ?? [];
  return (
    <>
      {query.isPending && <LoadingState message="Reading the queue…" />}
      {query.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(query.error)} />
      )}
      {query.isSuccess && rows.length === 0 && (
        <EmptyState icon="calendar-clock" title="Nothing here" body={empty} inline />
      )}
      {rows.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={onOpen}
          columns={[
            { key: 'code', label: 'Booking', mono: true },
            { key: 'when', label: 'Ceremony' },
            { key: 'status', label: 'State' },
            { key: 'clock', label: 'Clock', align: 'right', mono: true },
          ]}
          rows={rows.map((b) => ({
            id: b.id,
            code: b.public_code,
            when: ceremonyAt(b.scheduled_start_at),
            status: <StatusChip tone={statusTone(b.status)} label={statusLabel(b.status)} />,
            clock: clockTo(b.scheduled_start_at),
          }))}
        />
      )}
    </>
  );
}
